// ── LanguageSelectorScreen.jsx ────────────────────────────────────────────────
// Full-screen language picker shown on first visit.
// Disappears once the user clicks "Continue" and the choice is saved.

import React, { useState } from "react";
import { useLanguage } from "./LanguageContext";
import "./LanguageSelectorScreen.css";

// Simple inline SVG icons to avoid extra dependencies
const CheckIcon = () => (
  <svg viewBox="0 0 16 16" aria-hidden="true">
    <polyline points="3,8 7,12 13,4" />
  </svg>
);

const ArrowIcon = ({ rtl }) => (
  <svg
    viewBox="0 0 16 16"
    style={{ transform: rtl ? "rotate(180deg)" : "none" }}
    aria-hidden="true"
  >
    <polyline points="5,3 11,8 5,13" />
  </svg>
);

const LanguageSelectorScreen = () => {
  const { lang, setLang, setHasChosen } = useLanguage();
  const [selected, setSelected] = useState(lang); // pre-select current lang

  const handleContinue = () => {
    setLang(selected);
    setHasChosen(true);
  };

  return (
    <div className="lss-overlay" role="dialog" aria-modal="true">
      {/* Background effects */}
      <div className="lss-bg" aria-hidden="true">
        <div className="lss-glow-1" />
        <div className="lss-glow-2" />
        <div className="lss-grid" />
      </div>

      <div className="lss-card">
        {/* Brand */}
        <div className="lss-brand">
          <div className="lss-brand-dot" />
          <span className="lss-brand-name">
            Leaks<span>Mercy</span>
          </span>
          <div className="lss-brand-dot" />
        </div>

        {/* Header */}
        <p className="lss-welcome">Welcome / أهلاً بك</p>
        <h1 className="lss-title">
          {selected === "ar"
            ? "اختر لغتك المفضلة"
            : "Choose Your Preferred Language"}
        </h1>
        <p className="lss-subtitle">
          {selected === "ar"
            ? "يمكنك تغيير اللغة في أي وقت من شريط التنقل"
            : "You can change the language at any time from the navigation bar"}
        </p>

        <div className="lss-divider" />

        {/* Options */}
        <div className="lss-options">
          {/* Arabic */}
          <button
            className={`lss-option${selected === "ar" ? " selected" : ""}`}
            onClick={() => setSelected("ar")}
            aria-pressed={selected === "ar"}
          >
            <div className="lss-check" aria-hidden="true">
              <CheckIcon />
            </div>
            <span className="lss-flag" aria-hidden="true">🇸🇦</span>
            <span className="lss-lang-native">العربية</span>
            <span className="lss-lang-translated">Arabic</span>
            <span className="lss-rtl-badge">RTL</span>
          </button>

          {/* English */}
          <button
            className={`lss-option${selected === "en" ? " selected" : ""}`}
            onClick={() => setSelected("en")}
            aria-pressed={selected === "en"}
          >
            <div className="lss-check" aria-hidden="true">
              <CheckIcon />
            </div>
            <span className="lss-flag" aria-hidden="true">🇬🇧</span>
            <span className="lss-lang-native">English</span>
            <span className="lss-lang-translated">الإنجليزية</span>
            <span className="lss-rtl-badge" style={{ color: "rgba(0,180,255,0.8)", background: "rgba(0,180,255,0.1)", borderColor: "rgba(0,180,255,0.2)" }}>
              LTR
            </span>
          </button>
        </div>

        {/* Continue button */}
        <button
          className="lss-btn"
          onClick={handleContinue}
          disabled={!selected}
        >
          {selected === "ar" ? "متابعة" : "Continue"}
          <ArrowIcon rtl={selected === "ar"} />
        </button>
      </div>
    </div>
  );
};

export default LanguageSelectorScreen;
